import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Rating,
  Pagination,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  Hidden,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import styled from 'styled-components';

const SearchHeader = styled(Box)`
  background: linear-gradient(45deg, #2196f3 30%, #21cbf3 90%);
  padding: 40px 0;
  margin-bottom: 40px;
  color: white;
`;

const ProviderCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s;
  cursor: pointer;
  &:hover {
    transform: translateY(-5px);
  }
`;

const ProviderImage = styled(CardMedia)`
  height: 200px;
  background-size: cover;
  background-position: center;
`;

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    rating: '',
    sortBy: 'views',
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [openContact, setOpenContact] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    const fetchProviders = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          state: searchParams.get('state'),
          city: searchParams.get('city'),
          profession: searchParams.get('profession'),
          ...filters,
          page,
        });

        const response = await fetch(`/api/providers-search?${queryParams}`);
        if (!response.ok) throw new Error('Erro ao buscar profissionais');
        
        const data = await response.json();
        setProviders(data.providers);
        setTotalPages(data.totalPages);
      } catch (error) {
        setError('Erro ao buscar profissionais. Por favor, tente novamente.');
        console.error('Erro:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [searchParams, filters, page]);

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(1);
  };

  const handleProviderClick = (providerId) => {
    navigate(`/provider/${providerId}`);
  };

  const handleOpenContact = (provider) => {
    setSelectedProvider(provider);
    setOpenContact(true);
  };

  const handleCloseContact = () => {
    setOpenContact(false);
    setSelectedProvider(null);
  };

  return (
    <Box>
      <SearchHeader>
        <Container>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Avaliação Mínima</InputLabel>
                <Select
                  value={filters.rating}
                  onChange={(e) => handleFilterChange('rating', e.target.value)}
                  label="Avaliação Mínima"
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="4">4+ estrelas</MenuItem>
                  <MenuItem value="3">3+ estrelas</MenuItem>
                  <MenuItem value="2">2+ estrelas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  label="Ordenar por"
                >
                  <MenuItem value="rating">Melhor Avaliados</MenuItem>
                  <MenuItem value="reviews">Mais Avaliados</MenuItem>
                  <MenuItem value="recent">Mais Recentes</MenuItem>
                  <MenuItem value="views">Mais Clicados</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Container>
      </SearchHeader>

      <Container>
        <Box mb={4}>
          <Typography variant="h5" gutterBottom>
            Resultados para: {searchParams.get('profession')} em{' '}
            {searchParams.get('city')}, {searchParams.get('state')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {providers.length} profissionais encontrados
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {providers.map((provider) => (
                <Grid item xs={12} sm={6} md={4} key={provider._id}>
                  <ProviderCard onClick={() => handleProviderClick(provider._id)}>
                    <ProviderImage
                      image={provider.profileImage || '/default-profile.png'}
                      title={provider.name}
                    />
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6" gutterBottom noWrap>
                          {provider.name}
                        </Typography>
                        <Box display="flex" alignItems="center" color="text.secondary">
                          <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">{provider.views || 0}</Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <WorkIcon fontSize="small" sx={{ mr: 1 }} />
                        {provider.profession}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <LocationOnIcon fontSize="small" sx={{ mr: 1 }} />
                        {provider.address?.neighborhood ? `${provider.address.neighborhood}, ` : ''}
                        {provider.address?.city || provider.city}, {provider.address?.state || provider.state}
                      </Typography>
                      {provider.rating && (
                        <Box display="flex" alignItems="center" mt={1}>
                          <Rating value={provider.rating} readOnly size="small" />
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({provider.reviews?.length || 0} avaliações)
                          </Typography>
                        </Box>
                      )}
                      <MuiButton
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() => handleOpenContact(provider)}
                      >
                        Contratar
                      </MuiButton>
                    </CardContent>
                  </ProviderCard>
                </Grid>
              ))}
            </Grid>

            {/* Modal de contato */}
            <Dialog open={openContact} onClose={handleCloseContact}>
              <DialogTitle>Contato do Profissional</DialogTitle>
              <DialogContent>
                {selectedProvider && (
                  <>
                    <Typography variant="h6">{selectedProvider.name}</Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      Telefone: <strong>{selectedProvider.phone}</strong>
                    </Typography>
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <MuiButton onClick={handleCloseContact} color="primary">Fechar</MuiButton>
                {selectedProvider && (
                  <Hidden mdUp>
                    <MuiButton
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      component="a"
                      href={`https://wa.me/55${selectedProvider.phone.replace(/\D/g, '')}?text=Encontrei%20seu%20perfil%20do%20Site%20Contrato%20Seguro%2C%20gostaria%20de%20saber%20mais%20sobre%20seu%20servi%C3%A7o.`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      WhatsApp
                    </MuiButton>
                  </Hidden>
                )}
              </DialogActions>
            </Dialog>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={4}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default SearchResults; 